import React, { useEffect, useRef, useState } from 'react';

const TabAnimationMockup = () => {
  const browserElRef = useRef(null);
  const cursorElRef = useRef(null);
  const isMounted = useRef(true);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.getBoundingClientRect().width;
      if (width < 720) {
        setScale(width / 720);
      } else {
        setScale(1);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  useEffect(() => {
    isMounted.current = true;

    const browserEl = browserElRef.current;
    const cursorEl = cursorElRef.current;

    if (!browserEl || !cursorEl) return;

    const t0 = browserEl.querySelector('#t0');
    const t1 = browserEl.querySelector('#t1');
    const t2 = browserEl.querySelector('#t2');
    const t3 = browserEl.querySelector('#t3');
    const t4 = browserEl.querySelector('#t4');
    const tra = browserEl.querySelector('#tra');
    const urlEl = browserEl.querySelector('#url-text');
    const statusEl = browserEl.querySelector('#status-msg');
    const chaosView = browserEl.querySelector('#chaos-view');
    const raView = browserEl.querySelector('#ra-view');

    const tabs = [t0, t1, t2, t3, t4];

    const urls = [
      'https://jobnimbus.com/pipeline?view=board&filter=active&sort=modified...',
      'https://acculynx.com/jobs?status=open&page=2&user=all...',
      'https://eagleview.com/orders/report?id=EV-4471982&type=premium...',
      'https://xactanalysis.com/claims/workspace?claim=TX-2024-88341...',
      'https://app.companycam.com/projects?filter=recent&company=8842...',
      'https://roofingassist.ai'
    ];

    const msgs = [
      '5 tabs. 5 logins. 5 places your business is hiding from you.',
      'Closing JobNimbus...',
      'Closing AccuLynx...',
      'Closing EagleView...',
      'Closing Xactimate...',
      'Closing CompanyCam...',
      'Opening the command center...',
      'One tab. Every job. From storm to check.'
    ];

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    const removeActive = () => {
      tabs.forEach(tab => {
        if (tab) tab.classList.remove('active');
      });
      if (tra) tra.classList.remove('active');
    };

    async function runAnimation() {
      while (isMounted.current) {
        const startTime = Date.now();

        // Reset State
        if (tra) {
          tra.className = 'tab ra-tab';
          tra.style.display = 'none';
        }
        if (raView) {
          raView.style.display = 'none';
          raView.style.opacity = '0';
        }
        if (chaosView) {
          chaosView.style.opacity = '1';
          chaosView.style.display = 'grid';
        }
        if (urlEl) {
          urlEl.textContent = urls[0];
          urlEl.style.color = '#24292f';
        }
        if (statusEl) {
          statusEl.textContent = msgs[0];
        }
        if (browserEl) {
          browserEl.classList.remove('active-ra');
        }

        tabs.forEach((tab, index) => {
          if (tab) {
            tab.style.display = 'inline-flex';
            tab.className = 'tab';
            if (index === 0) tab.classList.add('active');
          }
        });

        if (cursorEl) {
          cursorEl.style.left = '450px';
          cursorEl.style.top = '180px';
          cursorEl.style.opacity = '1';
          cursorEl.style.display = 'block';
          cursorEl.className = 'virtual-cursor';
        }

        await sleep(2200);
        if (!isMounted.current) return;

        // Loop to close tabs one by one
        for (let i = 0; i < tabs.length; i++) {
          const el = tabs[i];

          if (el && cursorEl && browserEl) {
            const closeBtn = el.querySelector('.tab-close');
            if (closeBtn) {
              // Traverse up the offsetParent chain to get exact local coordinates relative to browserEl
              let targetX = 0;
              let targetY = 0;
              let current = closeBtn;
              while (current && current !== browserEl) {
                targetX += current.offsetLeft || 0;
                targetY += current.offsetTop || 0;
                current = current.offsetParent;
              }
              // Center of close button adjustment
              targetX += (closeBtn.offsetWidth / 2) - 2;
              targetY += (closeBtn.offsetHeight / 2) - 2;

              cursorEl.style.left = `${targetX}px`;
              cursorEl.style.top = `${targetY}px`;

              await sleep(450);
              if (!isMounted.current) return;
              cursorEl.classList.add('clicking');
              await sleep(80);
              if (!isMounted.current) return;
            }
          }

          if (statusEl) statusEl.textContent = msgs[i + 1];
          if (urlEl) urlEl.textContent = urls[i + 1] || urls[i];
          if (el) {
            el.classList.add('closing');
            el.classList.remove('active');
          }

          // Shift active tab
          const nextEl = tabs[i + 1];
          if (nextEl) {
            nextEl.classList.add('active');
          } else if (urlEl) {
            urlEl.style.color = '#D45C1A';
          }

          await sleep(150);
          if (!isMounted.current) return;
          if (cursorEl) cursorEl.classList.remove('clicking');
          await sleep(400);
          if (!isMounted.current) return;
          if (el) el.style.display = 'none';
          await sleep(150);
          if (!isMounted.current) return;
        }

        // Fade out cursor
        if (cursorEl && browserEl) {
          cursorEl.style.left = '480px';
          cursorEl.style.top = '250px';
          cursorEl.style.opacity = '0';
          await sleep(450);
          if (!isMounted.current) return;
          cursorEl.style.display = 'none';
        }

        await sleep(150);
        if (!isMounted.current) return;
        if (statusEl) statusEl.textContent = msgs[6];
        if (urlEl) {
          urlEl.textContent = urls[5];
          urlEl.style.color = '#D45C1A';
        }

        if (tra) {
          tra.style.display = 'inline-flex';
          // Trigger reflow
          const _reflow = tra.clientHeight;
          tra.className = 'tab ra-tab opening active';
        }

        await sleep(450);
        if (!isMounted.current) return;
        if (chaosView) chaosView.style.opacity = '0';
        await sleep(550);
        if (!isMounted.current) return;
        if (chaosView) chaosView.style.display = 'none';
        if (raView) {
          raView.style.display = 'block';
          raView.style.opacity = '0';
        }

        await sleep(100);
        if (!isMounted.current) return;
        if (raView) {
          raView.style.transition = 'opacity 0.8s';
          raView.style.opacity = '1';
        }
        if (browserEl) {
          browserEl.classList.add('active-ra');
        }

        await sleep(900);
        if (!isMounted.current) return;
        if (statusEl) statusEl.textContent = msgs[7];

        const elapsed = Date.now() - startTime;
        const remaining = 20000 - elapsed;
        if (remaining > 0) {
          await sleep(remaining);
        } else {
          await sleep(1000);
        }
      }
    }

    runAnimation();

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="browser-scale-wrapper" 
      style={{ 
        width: '100%', 
        overflow: 'hidden', 
        height: `${400 * scale}px`,
        position: 'relative'
      }}
    >
      <div 
        ref={browserElRef} 
        className="browser"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: '720px',
          minWidth: '720px',
          height: '400px',
          position: 'absolute',
          top: 0,
          left: 0,
          margin: 0
        }}
      >
        {/* Virtual Cursor overlay */}
        <div ref={cursorElRef} className="virtual-cursor" id="virtual-cursor">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M4.5 3V19L9.2 14.3L12.5 21L15.3 19.6L12 13L17.7 13L4.5 3Z" stroke="white" strokeWidth="1.5" strokeLinejoin="miter" />
        </svg>
      </div>

      <div className="chrome">
        <div className="window-controls">
          <span className="control-dot dot-red"></span>
          <span className="control-dot dot-yellow"></span>
          <span className="control-dot dot-green"></span>
        </div>

        <div className="tab active" id="t0">
          {/* JobNimbus Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#1b365d" />
            <path d="M7 6H10V14.5C10 15.88 11.12 17 12.5 17C13.88 17 15 15.88 15 14.5V6H18V14.5C18 17.54 15.54 20 12.5 20C9.46 20 7 17.54 7 14.5V6Z" fill="#8abecc" />
          </svg>
          <span>JobNimbus</span>
          <div className="tab-close">✕</div>
        </div>
        <div className="tab" id="t1">
          {/* AccuLynx Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#58201a" />
            <path d="M12 5L6 10V18H10V13H14V18H18V10L12 5Z" fill="#ff7f6e" />
          </svg>
          <span>AccuLynx</span>
          <div className="tab-close">✕</div>
        </div>
        <div className="tab" id="t2">
          {/* EagleView Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#143c4a" />
            <path d="M12 4L4 12H9L12 8L15 12H20L12 4Z" fill="#4ad7ff" />
          </svg>
          <span>EagleView</span>
          <div className="tab-close">✕</div>
        </div>
        <div className="tab" id="t3">
          {/* Xactimate Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#32245c" />
            <path d="M6 18H9V11H6V18ZM11 18H14V6H11V18ZM16 18H19V14H16V18Z" fill="#c3aeff" />
          </svg>
          <span>Xactimate</span>
          <div className="tab-close">✕</div>
        </div>
        <div className="tab" id="t4">
          {/* CompanyCam Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#114530" />
            <circle cx="12" cy="12" r="6" stroke="#48dca3" strokeWidth="2.5" />
            <circle cx="12" cy="12" r="2.5" fill="#48dca3" />
          </svg>
          <span>CompanyCam</span>
          <div className="tab-close">✕</div>
        </div>
        <div className="tab ra-tab" id="tra">
          {/* RoofingAssist.AI Glowing Favicon */}
          <svg className="favicon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#D45C1A" />
            <path d="M12 4L5 11H9V19L19 11H15L12 4Z" fill="#FFFFFF" />
          </svg>
          <span style={{ fontFamily: "'Georgia', serif", fontSize: '12px', fontWeight: 400, color: '#24292f', letterSpacing: '0.01em' }}>
            Roofing<span style={{ fontWeight: 700, color: '#D45C1A' }}>Assist.AI</span>
          </span>
        </div>

        <div className="new-tab-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </div>
      </div>

      <div className="addressbar">
        <div className="nav-actions">
          <button className="nav-action-btn disabled" disabled>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          </button>
          <button className="nav-action-btn disabled" disabled>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </button>
          <button className="nav-action-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"></path></svg>
          </button>
          <button className="nav-action-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          </button>
        </div>

        <div className="url-input-container">
          <div className="url-security-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
            </svg>
          </div>
          <div className="url-text" id="url-text">https://jobnimbus.com/pipeline?view=board&filter=active&sort=modified...</div>
          <div className="url-actions">
            <button className="url-action-btn" title="Bookmark This Page">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
            </button>
          </div>
        </div>

        <div className="toolbar-utilities">
          <button className="nav-action-btn" title="Extensions">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
          </button>
          <div className="toolbar-avatar">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
          <button className="nav-action-btn" title="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>
          </button>
        </div>
      </div>

      <div className="content">
        {/* Chaos view with multiple separate panels */}
        <div className="chaos-view" id="chaos-view">
          <div className="chaos-panel">
            <div className="chaos-label">Pipeline</div>
            <div className="skel" style={{ width: '75%' }}></div>
            <div className="skel" style={{ width: '90%' }}></div>
            <div className="skel" style={{ width: '55%' }}></div>
            <div className="skel accent" style={{ width: '70%' }}></div>
            <div className="skel" style={{ width: '80%' }}></div>
            <div className="skel" style={{ width: '40%' }}></div>
          </div>
          <div className="chaos-panel">
            <div className="chaos-label">Measurements</div>
            <div style={{ height: '60px', background: '#e1e4e8', borderRadius: '3px', marginBottom: '12px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '12px', left: '8px', right: '8px', height: '1px', background: '#cfd5db', transform: 'rotate(3deg)' }}></div>
              <div style={{ position: 'absolute', top: '28px', left: '8px', right: '8px', height: '1px', background: '#cfd5db', transform: 'rotate(-2deg)' }}></div>
              <div style={{ position: 'absolute', top: '44px', left: '8px', right: '8px', height: '1px', background: '#cfd5db', transform: 'rotate(1deg)' }}></div>
            </div>
            <div className="skel" style={{ width: '60%' }}></div>
            <div className="skel" style={{ width: '80%' }}></div>
          </div>
          <div className="chaos-panel">
            <div className="chaos-label">Estimate</div>
            <div className="skel" style={{ width: '85%' }}></div>
            <div className="skel" style={{ width: '65%' }}></div>
            <div className="skel" style={{ width: '90%' }}></div>
            <div className="skel accent" style={{ width: '50%' }}></div>
            <div className="skel" style={{ width: '70%' }}></div>
            <div className="skel" style={{ width: '45%' }}></div>
          </div>
        </div>

        {/* RoofingAssist.AI Integrated Dashboard View */}
        <div className="ra-view" id="ra-view">
          <div className="ra-metrics-grid">
            <div className="ra-metric" style={{ animationDelay: '0.05s' }}>
              <div className="ra-metric-label">Revenue MTD</div>
              <div className="ra-metric-value">$247,800</div>
              <div className="ra-metric-sub">42 jobs closed</div>
            </div>
            <div className="ra-metric" style={{ animationDelay: '0.12s' }}>
              <div className="ra-metric-label">Open jobs</div>
              <div className="ra-metric-value">61</div>
              <div className="ra-metric-sub">14 active today</div>
            </div>
            <div className="ra-metric" style={{ animationDelay: '0.19s' }}>
              <div className="ra-metric-label">Avg margin</div>
              <div className="ra-metric-value">34.2%</div>
              <div className="ra-metric-sub">vs 31.8% last mo.</div>
            </div>
            <div className="ra-metric" style={{ animationDelay: '0.26s' }}>
              <div className="ra-metric-label">Storm alerts</div>
              <div className="ra-metric-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>3 <div className="radar-dot-pulse"></div></div>
              <div className="ra-metric-sub">Dallas metro · live</div>
            </div>
          </div>

          <div className="ra-details-grid">
            <div className="ra-metric" style={{ animationDelay: '0.33s', paddingBottom: '4px' }}>
              <div className="ra-metric-label" style={{ marginBottom: '7px' }}>Job pipeline</div>
              <div className="job-row-mini" style={{ animationDelay: '0.38s' }}>
                <div className="job-status-mini" style={{ background: '#D45C1A' }}></div>
                <span className="job-name-mini">Martinez — 4518 Elm</span>
                <span className="job-val-mini">$14,320</span>
              </div>
              <div className="job-row-mini" style={{ animationDelay: '0.42s' }}>
                <div className="job-status-mini" style={{ background: '#D45C1A' }}></div>
                <span className="job-name-mini">Hendricks — 221 Oak</span>
                <span className="job-val-mini">$9,840</span>
              </div>
              <div className="job-row-mini" style={{ animationDelay: '0.46s' }}>
                <div className="job-status-mini" style={{ background: '#27c93f' }}></div>
                <span className="job-name-mini">Reyes — 1104 Main</span>
                <span className="job-val-mini">$22,100</span>
              </div>
              <div className="job-row-mini" style={{ animationDelay: '0.50s' }}>
                <div className="job-status-mini" style={{ background: '#2d9cdb' }}></div>
                <span className="job-name-mini">Thompson — 87 Pine</span>
                <span className="job-val-mini">$11,560</span>
              </div>
              <div className="job-row-mini" style={{ animationDelay: '0.54s', border: 'none' }}>
                <div className="job-status-mini" style={{ background: '#828282' }}></div>
                <span className="job-name-mini">Wallace — 332 Birch</span>
                <span className="job-val-mini">$8,200</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="ra-metric" style={{ animationDelay: '0.36s' }}>
                <div className="ra-metric-label">Latest estimate</div>
                <div className="ra-metric-value" style={{ fontSize: '11px' }}>2,184 sq ft · 6/12 pitch</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                  <span style={{ fontSize: '9px', color: '#57606a' }}>Materials</span>
                  <span style={{ fontSize: '9px', fontFamily: "'Consolas', monospace", color: '#1c2836' }}>$8,940</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '9px', color: '#57606a' }}>Labor</span>
                  <span style={{ fontSize: '9px', fontFamily: "'Consolas', monospace", color: '#1c2836' }}>$4,200</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e1e4e8', marginTop: '4px', paddingTop: '4px' }}>
                  <span style={{ fontSize: '9px', color: '#D45C1A', fontWeight: '500' }}>Total</span>
                  <span style={{ fontSize: '10px', fontFamily: "'Consolas', monospace", color: '#D45C1A', fontWeight: '500' }}>$14,320</span>
                </div>
              </div>

              <div className="ra-metric" style={{ animationDelay: '0.40s' }}>
                <div className="ra-metric-label">Crew status</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0', borderBottom: '1px solid #f1f3f5', fontSize: '9px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#D45C1A', animation: 'pulse 1.5s infinite', marginRight: '4px', flexShrink: 0 }}></div>
                  <span style={{ color: '#24292f', fontWeight: 500, flex: 1 }}>Crew A — 4518 Elm</span>
                  <span style={{ color: '#57606a' }}>active</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0', fontSize: '9px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3a8a6a', marginRight: '4px', flexShrink: 0 }}></div>
                  <span style={{ color: '#24292f', fontWeight: 500, flex: 1 }}>Crew B — en route</span>
                  <span style={{ color: '#57606a' }}>12m out</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="status-bar">
        <span id="status-msg">5 tabs. 5 logins. 5 places your business is hiding from you.</span>
      </div>
    </div>
  </div>
  );
};

export default TabAnimationMockup;
